import React, {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {Controller, useFieldArray, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {FiTrash2} from 'react-icons/fi'
import _ from 'lodash'
import {
  VmoGridColumn,
  VmoFormField,
  VmoButton,
  VmoForm,
  VmoCard,
  VmoCardContent,
  VmoGrid,
  VmoCheckbox,
  VmoLabel,
  VmoFormSelect,
  VmoFormInput,
  VmoTooltip,
} from 'vmo-library'
import {
  EDIT_RULE,
  CREATE_RULE,
  GET_RULE,
  GET_TAGS,
  GET_USERS,
  GET_ALL_MAIL_INBOX,
  LIST_COMPANY_CONTACTS,
  GET_ALL_CONTACTS_IDS,
} from 'actions/types'
import {createRule, getRule, editRule, getRules} from 'actions/settings_automation'
import {getAllMailInbox, getStatusList} from 'actions/inbox'
import {getAllContactsIds, listCompaniesInContacts} from 'actions/contacts'
import {getUsers, getTags} from 'actions/settings'
import Page from 'components/common/Page'
import {getPathWithPrefix, startCase} from 'utils/helper'
import {countryList, priorityList} from 'constants/variables'
import {ruleSchema} from 'validation/settings/automations/rules.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {INBOX_TYPES} from 'constants/enums'
import {SETTINGS_PATH} from 'constants/path'
import useApiResponse from 'hooks/impure/useApiResponse'
import {Input} from 'components/common/Form'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'
import {IF_CONTACTS_OPTIONS, IF_OPTIONS, THEN_CONTACTS_OPTIONS, THEN_OPTIONS, WHEN_OPTIONS} from './RulesConstants'

const defaultValues = {
  rule: {
    name: '',
    entity: null,
    trigger: null,
  },
  ifConditionList: [{condition: null, value: null}],
  thenConditionList: [{then: null, action: null, thenData: null}],
}

const RuleForm = props => {
  const navigate = useNavigate()
  const {editId, toggleCreate} = props

  const dispatch = useDispatch()

  const {
    handleSubmit,
    formState: {errors, isDirty},
    clearErrors,
    control,
    watch,
    reset,
    setValue,
    getValues,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(ruleSchema),
    defaultValues,
  })

  const watchEntity = watch('rule.entity')
  const watchTrigger = watch('rule.trigger')

  const {
    fields: ifConditionList,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'ifConditionList',
  })

  const {
    fields: thenConditionList,
    append: thenAppend,
    remove: thenRemove,
  } = useFieldArray({
    control,
    name: 'thenConditionList',
  })

  const {rule} = useSelector(state => state.settingsAutomations)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)
  const {statusList = []} = useSelector(state => state.emailInbox)

  const [ifDropDownList, setIfDropdownList] = useState([])
  const [thenDropDownList, setThenDropdownList] = useState([])
  const [dropdownData, setDropdownData] = useState({
    tag: [],
    assigneeId: [],
    mailList: [],
    contactList: [],
    countryList: [],
    companyList: [],
  })

  const {setWarningModal} = useUnsavedChanges()
  const {workPermissions} = useUserPermissions()
  const allowManageAutomationRulesRole = workPermissions?.manage_automation_rules

  const {settingsLearnMoreUrl} = useLearnMoreUrl()
  const addWorkflowRulesLearnMore = settingsLearnMoreUrl?.automations?.workflowRules?.addLearnMore
  const updateWorkflowRulesLearnMore = settingsLearnMoreUrl?.automations?.workflowRules?.updateLearnMore

  useApiResponse({
    action: getAllMailInbox,
    enabled: true,
    label: GET_ALL_MAIL_INBOX,
    storePath: 'mailInbox.inbox',
    onSuccess: mailboxList => {
      const list = mailboxList
        ?.filter(inbox => (inbox.type === INBOX_TYPES.EMAIL ? inbox.emailVerified : true))
        ?.map(inbox => ({
          value: inbox.id,
          text: inbox.name,
          key: inbox.id,
        }))
      setDropdownData(dropdownData => ({
        ...dropdownData,
        mailList: list || [],
      }))
    },
  })

  useApiResponse({
    action: getTags,
    enabled: true,
    label: GET_TAGS,
    storePath: 'settings.tagList',
    onSuccess: tagList => {
      const list = tagList?.map(({color, id, label}) => ({
        value: id,
        key: id,
        text: label,
        label: {
          color,
          empty: true,
          circular: true,
        },
      }))

      setDropdownData(dropdownData => ({
        ...dropdownData,
        tag: list || [],
      }))
    },
  })

  useApiResponse({
    action: getUsers,
    enabled: true,
    label: GET_USERS,
    storePath: 'settings.getUsersList',
    onSuccess: getUsersList => {
      const list = getUsersList?.users?.map(list => ({
        value: list.id,
        text: `${list.firstName} ${list.lastName}`,
        key: list.id,
      }))
      setDropdownData(dropdownData => ({
        ...dropdownData,
        assigneeId: list || [],
      }))
    },
  })

  useApiResponse({
    action: getAllContactsIds,
    enabled: true,
    label: GET_ALL_CONTACTS_IDS,
    storePath: 'contacts.contactsIdsList',
    onSuccess: contactsIdsList => {
      const list = contactsIdsList?.map(({id, name, email}) => ({
        key: id,
        value: id,
        text: name || '-',
        content: (
          <div>
            {name || '-'}
            <small className="ml-2 vmo-lead">({email})</small>
          </div>
        ),
      }))

      setDropdownData(dropdownData => ({
        ...dropdownData,
        contactList: list || [],
      }))
    },
  })

  useApiResponse({
    action: listCompaniesInContacts,
    enabled: true,
    label: LIST_COMPANY_CONTACTS,
    storePath: 'contacts.companyList',
    onSuccess: companyList => {
      const list = companyList?.map(({id, name}) => ({
        key: id,
        value: id,
        text: name,
      }))
      setDropdownData(dropdownData => ({
        ...dropdownData,
        companyList: list || [],
      }))
    },
  })

  const resetForm = () => {
    if (editId) {
      const {
        rule: {name, entity, trigger},
        actions,
        criterias,
      } = rule
      setValue('rule', {name, entity, trigger})

      const ifArr = []
      for (const [key, value] of Object.entries(criterias[0])) {
        ifArr.push({condition: key, value})
      }

      setValue('ifConditionList', ifArr)
      setValue(
        'thenConditionList',
        actions.map(ite => {
          const acc = {}
          for (const [key, value] of Object.entries(ite)) {
            if (key === 'action') {
              acc[key] = value
            } else {
              acc.then = key
              acc.thenData = value
            }
          }
          return acc
        })
      )
      setWarningModal(false)
    } else {
      reset(defaultValues)
    }
  }

  const submitRule = ({rule, ifConditionList, thenConditionList}) => {
    const dataToDispatch = {
      rule,
      criterias: [
        ifConditionList?.reduce((map, {condition, value}) => {
          map[condition] = value
          return map
        }, {}),
      ],
      actions: thenConditionList?.map(({action, then, thenData}) => ({
        [then]: thenData || [],
        action: action || '',
      })),
    }

    if (editId) {
      setWarningModal(false)
      dispatch(editRule(editId, dataToDispatch))
    } else {
      dispatch(createRule(dataToDispatch))
    }
  }

  const currentIfCondition = getValues('ifConditionList')
  const currentThenCondition = getValues('thenConditionList')

  useEffect(() => {
    dispatch(getStatusList())
  }, [dispatch])

  useEffect(() => {
    if (editId) dispatch(getRule(editId))
  }, [editId, dispatch])

  useEffect(() => {
    if (editId && isDirty) {
      setWarningModal(true)
    }
    return () => {
      setWarningModal(false)
    }
  }, [isDirty, editId, setWarningModal])

  useEffect(() => {
    const list = countryList.map(({value, text}) => {
      return {
        value,
        key: value,
        text,
      }
    })
    setDropdownData(dropdownData => ({
      ...dropdownData,
      countryList: list,
    }))
  }, [])

  useEffect(() => {
    if (successLabels.includes(GET_RULE)) {
      resetForm()
    }
    if (successLabels.includes(CREATE_RULE)) {
      dispatch(getRules())
      toggleCreate(false)
    }
    if (successLabels.includes(EDIT_RULE)) {
      navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.WORKFLOWS))
    }
  }, [successLabels, dispatch, navigate, toggleCreate])

  useEffect(() => {
    if (watchEntity === 'incoming') {
      setIfDropdownList(IF_OPTIONS)
      setThenDropdownList(THEN_OPTIONS)
    } else if (watchEntity === 'outgoing') {
      setIfDropdownList(IF_OPTIONS.filter((_, i) => [0, 2, 3, 7, 8].includes(i)))
      setThenDropdownList(THEN_OPTIONS)
    } else if (watchEntity === 'contacts' && watchTrigger === 'created') {
      // setIfDropdownList(_.take(IF_CONTACTS_OPTIONS, 8))
      setIfDropdownList(_.take(IF_CONTACTS_OPTIONS, 5))
      setThenDropdownList(THEN_CONTACTS_OPTIONS)
    } else if (watchEntity === 'contacts' && watchTrigger === 'updated') {
      setIfDropdownList(IF_CONTACTS_OPTIONS)
      setThenDropdownList(THEN_CONTACTS_OPTIONS)
    } else {
      setIfDropdownList([])
      setThenDropdownList([])
    }
  }, [watchEntity, watchTrigger])

  useEffect(() => {
    if (watchEntity === 'incoming' || watchEntity === 'outgoing') setValue('rule.trigger', watchEntity)
  }, [watchEntity, setValue])

  const renderIfField = (condition, index, defaultVal) => {
    if (
      [
        'mailboxId',
        'haveTags',
        'doNotHaveTags',
        'contactId',
        'assigneeId',
        'country',
        'contactTypeId',
        'labelIds',
      ].includes(condition)
    ) {
      let optionsList
      let isMultiple = false
      switch (condition) {
        case 'mailboxId':
          optionsList = dropdownData.mailList
          break
        case 'haveTags':
        case 'doNotHaveTags':
          optionsList = dropdownData.tag
          isMultiple = true
          break
        case 'contactId':
          optionsList = dropdownData.contactList
          break
        case 'assigneeId':
          optionsList = dropdownData.contactList
          break
        case 'country':
          optionsList = dropdownData.countryList
          break
        case 'contactTypeId':
          optionsList = [
            {
              key: 'Potential Customer',
              value: 'Potential Customer',
              text: 'Potential Customer',
            },
            {
              key: 'Current Customer',
              value: 'Current Customer',
              text: 'Current Customer',
            },
            {key: 'Others', value: 'Others', text: 'Others'},
          ]
          break
        case 'labelIds':
          optionsList = dropdownData.tag
          break
        default:
          break
      }
      return (
        <Controller
          control={control}
          name={`ifConditionList[${index}].value`}
          defaultValue={defaultVal}
          render={({field: {value, onChange}}) => (
            <VmoFormSelect
              key={optionsList}
              multiple={isMultiple}
              value={value}
              options={optionsList}
              onClick={() => clearErrors(`ifConditionList[${index}].value`)}
              onChange={(e, {value}) => {
                onChange(value)
              }}
              error={
                _.get(errors, `ifConditionList[${index}][value]`) && {
                  content: 'Please Fill Required Field',
                }
              }
            />
          )}
        />
      )
    }

    if (
      [
        'customerEmailDomain',
        'subjectLike',
        'contactEmail',
        'company',
        'contactEmailDomain',
        'firstName',
        'lastName',
        'title',
        'description',
        'interactions',
      ].includes(condition)
    ) {
      return (
        <Controller
          control={control}
          name={`ifConditionList[${index}].value`}
          defaultValue={defaultVal}
          render={({field: {value, onChange}}) => (
            <VmoFormInput
              value={value}
              onChange={e => onChange(e.target.value)}
              error={
                _.get(errors, `ifConditionList[${index}][value]`) && {
                  content: 'Please Fill Required Field',
                }
              }
            />
          )}
        />
      )
    }

    return <>something went wrong</>
  }

  const renderThenField = (then, index, thenData) => {
    if (
      [
        'assigneeId',
        'statusId',
        'addLabelIds',
        'removeLabelIds',
        'mailboxId',
        'priority',
        'labelIds',
        'companyId',
      ].includes(then)
    ) {
      let option
      let isMultiple
      switch (then) {
        case 'assigneeId':
          option = dropdownData.assigneeId
          break
        case 'statusId':
          option = statusList.map(status => ({
            value: status.id,
            key: status.id,
            text: startCase(status.status),
          }))
          break
        case 'addLabelIds':
        case 'removeLabelIds':
        case 'labelIds':
          option = dropdownData.tag
          isMultiple = true
          break
        case 'mailboxId':
          option = dropdownData.mailList
          break
        case 'priority':
          option = priorityList
          break
        case 'companyId':
          option = dropdownData.companyList
          break
        default:
          break
      }
      return (
        <Controller
          control={control}
          name={`thenConditionList[${index}].thenData`}
          defaultValue={thenData}
          render={({field: {value, onChange}}) => (
            <VmoFormSelect
              value={value}
              multiple={isMultiple}
              options={option}
              onClick={() => clearErrors(`thenConditionList[${index}].thenData`)}
              onChange={(e, {value}) => onChange(value)}
              error={
                _.get(errors, `thenConditionList[${index}].thenData`) && {
                  content: 'Please Fill Required Field',
                }
              }
            />
          )}
        />
      )
    }

    if (['website', 'description', 'title'].includes(then)) {
      return (
        <Controller
          control={control}
          defaultValue={thenData}
          name={`thenConditionList[${index}].thenData`}
          render={({field: {value, onChange}}) => (
            <VmoFormInput
              value={value}
              onChange={e => onChange(e.target.value)}
              error={
                _.get(errors, `thenConditionList[${index}].thenData`) && {
                  content: 'Please Fill Required Field',
                }
              }
            />
          )}
        />
      )
    }

    if (then === 'note') {
      return (
        <Controller
          control={control}
          name={`thenConditionList[${index}].thenData`}
          defaultValue={thenData}
          render={({field: {value, onChange}}) => (
            <VmoFormInput
              value={value}
              onChange={e => onChange(e.target.value)}
              error={
                _.get(errors, `thenConditionList[${index}][thenData]`) && {
                  content: 'Please Fill Required Field',
                }
              }
            />
          )}
        />
      )
    }
  }

  return (
    <>
      <Page
        header={editId ? 'Edit Rule' : 'Create Rule'}
        learnMoreUrl={editId ? updateWorkflowRulesLearnMore : addWorkflowRulesLearnMore}
        headerbackbtn={{
          onClick: () => {
            if (editId) {
              setWarningModal(false)
              navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.WORKFLOWS))
              resetForm()
            } else {
              toggleCreate(false)
            }
          },
        }}
        subHeader="Create your customised workflow rule."
        table={false}
        headerDivider
        lockRole={!allowManageAutomationRulesRole}
        actionButton={{
          cancel: {
            content: 'Cancel',
            disabled: isLoadingData && (type.includes(CREATE_RULE) || type.includes(EDIT_RULE)),
            onClick: () => {
              if (editId) {
                setWarningModal(false)
                navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.WORKFLOWS))
                resetForm()
              } else {
                toggleCreate(false)
              }
            },
          },
          success: {
            content: editId ? 'Update' : 'Create',
            loading: isLoadingData && (type.includes(CREATE_RULE) || type.includes(EDIT_RULE)),
            disabled: !isDirty || (isLoadingData && (type.includes(CREATE_RULE) || type.includes(EDIT_RULE))),
            ...(allowManageAutomationRulesRole && {
              onClick: handleSubmit(submitRule),
            }),
          },
        }}
      >
        <VmoCard fluid className="switchBox">
          <VmoCardContent>
            <VmoForm className="errorLabel">
              <Input
                name="rule.name"
                control={control}
                label="Rule Name"
                tooltipProps={{content: 'Specify name of the workflow rule'}}
                maxLength={30}
                required
                fluid
                placeholder="Rule Name..."
                width={8}
              />
              <VmoFormField required>
                <div className="info-header">
                  <label>When</label>
                  <VmoTooltip
                    content="Set the event or trigger that initiates the workflow"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                <Controller
                  name="rule.entity"
                  control={control}
                  render={({field: {value, onChange}}) => (
                    <VmoFormSelect
                      width={8}
                      fluid
                      placeholder="Select Entity"
                      options={WHEN_OPTIONS}
                      value={value}
                      onChange={(e, {value}) => {
                        onChange(value)
                        setValue('ifConditionList', [{condition: null, value: null}])
                        setValue('thenConditionList', [{then: null, action: null, thenData: null}])
                        clearErrors(['rule.trigger', 'ifConditionList', 'thenConditionList'])
                      }}
                      onClick={() => clearErrors('rule.entity')}
                      error={
                        errors.rule?.entity && {
                          content: errors.rule.entity.message,
                        }
                      }
                    />
                  )}
                />
              </VmoFormField>
              {watchEntity === 'contacts' && (
                <VmoFormField required>
                  <Controller
                    name="rule.trigger"
                    control={control}
                    render={({field: {value, onChange}}) => (
                      <VmoFormSelect
                        width={8}
                        fluid
                        placeholder="Select Trigger"
                        options={[
                          {
                            key: 'created',
                            value: 'created',
                            text: 'Created',
                          },
                          {
                            key: 'updated',
                            value: 'updated',
                            text: 'Updated',
                          },
                        ]}
                        value={value}
                        onChange={(e, {value}) => {
                          onChange(value)
                          setValue('ifConditionList', [{condition: null, value: null}])
                          setValue('thenConditionList', [
                            {
                              then: null,
                              action: null,
                              thenData: null,
                            },
                          ])
                          clearErrors(['ifConditionList', 'thenConditionList'])
                        }}
                        onClick={() => clearErrors('rule.trigger')}
                        error={
                          errors.rule?.trigger && {
                            content: errors.rule.trigger.message,
                          }
                        }
                      />
                    )}
                  />
                </VmoFormField>
              )}
            </VmoForm>
          </VmoCardContent>
        </VmoCard>
        <VmoCard fluid className="switchBox mt-1">
          <VmoCardContent>
            <VmoForm className="errorLabel">
              <VmoFormField required>
                <div className="info-header">
                  <label>If</label>
                  <VmoTooltip
                    content="Set criteria that need to be met for workflow to proceed"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                {ifConditionList.map(({id, condition, value}, index) => {
                  const conditionValue = getValues(`ifConditionList[${index}].condition`)
                  return (
                    <VmoGrid key={id}>
                      <VmoGridColumn width={5}>
                        <Controller
                          control={control}
                          name={`ifConditionList[${index}].condition`}
                          defaultValue={condition}
                          render={({field: {value, onChange}}) => (
                            <VmoFormSelect
                              value={value}
                              options={ifDropDownList}
                              onClick={() => clearErrors(`ifConditionList[${index}].condition`)}
                              onChange={(e, {value}) => {
                                onChange(value)
                                setValue(`ifConditionList[${index}].value`, null)
                                setIfDropdownList(ifDropDownList =>
                                  ifDropDownList.map(dropdownItem => {
                                    return dropdownItem.value === value
                                      ? {
                                          ...dropdownItem,
                                          disabled: true,
                                        }
                                      : {
                                          ...dropdownItem,
                                          disabled: !!currentIfCondition
                                            .map(({condition}) => condition)
                                            .includes(dropdownItem.value),
                                        }
                                  })
                                )
                                clearErrors(`ifConditionList[${index}].value`)
                              }}
                              error={
                                _.get(errors, `ifConditionList[${index}][condition]`) && {
                                  content: errors.ifConditionList[index].condition.message,
                                }
                              }
                            />
                          )}
                        />
                      </VmoGridColumn>
                      <VmoGridColumn width={10}>
                        {conditionValue && renderIfField(conditionValue, index, value)}
                      </VmoGridColumn>

                      {ifConditionList.length > 1 && (
                        <VmoGridColumn width={1}>
                          <VmoTooltip
                            content="Delete"
                            trigger={
                              <VmoButton
                                icon
                                className="bg-btn-color"
                                onClick={() => {
                                  setIfDropdownList(ifDropDownList =>
                                    ifDropDownList.map(dropdownItem =>
                                      dropdownItem.value === conditionValue
                                        ? {
                                            ...dropdownItem,
                                            disabled: false,
                                          }
                                        : dropdownItem
                                    )
                                  )
                                  remove(index)
                                }}
                              >
                                <FiTrash2 />
                              </VmoButton>
                            }
                          />
                        </VmoGridColumn>
                      )}

                      {index !== ifConditionList.length - 1 && (
                        <div
                          style={{
                            width: '100%',
                            textAlign: 'center',
                            margin: '12px 0',
                          }}
                        >
                          <VmoLabel>AND</VmoLabel>
                        </div>
                      )}
                    </VmoGrid>
                  )
                })}
                {ifConditionList.length < 2 && (
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      margin: '12px 0',
                    }}
                  >
                    <VmoButton size="mini" color="light" basic onClick={() => append({condition: null, value: null})}>
                      Add Condition
                    </VmoButton>
                  </div>
                )}
              </VmoFormField>
            </VmoForm>
          </VmoCardContent>
        </VmoCard>
        {/* Then Card */}
        <VmoCard fluid className="switchBox">
          <VmoCardContent>
            <VmoForm className="errorLabel">
              <VmoFormField required>
                <div className="info-header">
                  <label>Then</label>
                  <VmoTooltip
                    content="Set actions that will be taken if the criteria met"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                {thenConditionList.map(({id, thenData, then}, index) => {
                  const thenValue = getValues(`thenConditionList[${index}].then`)
                  return (
                    <VmoGrid key={id}>
                      <VmoGridColumn width={5}>
                        <Controller
                          control={control}
                          name={`thenConditionList[${index}].then`}
                          defaultValue={then}
                          render={({field: {value, onChange}}) => (
                            <VmoFormSelect
                              options={thenDropDownList}
                              value={value}
                              onClick={() => clearErrors(`thenConditionList[${index}].then`)}
                              onChange={(e, data) => {
                                onChange(data.value)
                                setValue(
                                  `thenConditionList[${index}].action`,
                                  data.options.find(op => op.value === data.value).action
                                )
                                setValue(`thenConditionList[${index}].thenData`, null)
                                setThenDropdownList(thenDropDownList =>
                                  thenDropDownList.map(dropdownItem => {
                                    return dropdownItem.value === data.value
                                      ? {
                                          ...dropdownItem,
                                          disabled: true,
                                        }
                                      : {
                                          ...dropdownItem,
                                          disabled: !!currentThenCondition
                                            .map(({then}) => then)
                                            .includes(dropdownItem.value),
                                        }
                                  })
                                )
                                clearErrors(`thenConditionList[${index}].thenData`)
                              }}
                              error={
                                _.get(errors, `thenConditionList[${index}].then`) && {
                                  content: errors.thenConditionList[index].then.message,
                                }
                              }
                            />
                          )}
                        />
                      </VmoGridColumn>

                      <VmoGridColumn width={10}>
                        {thenValue && renderThenField(thenValue, index, thenData)}
                      </VmoGridColumn>

                      {thenConditionList.length > 1 && (
                        <VmoGridColumn width={1}>
                          <VmoTooltip
                            content="Delete"
                            trigger={
                              <VmoButton
                                icon
                                className="bg-btn-color"
                                onClick={() => {
                                  setIfDropdownList(ifDropDownList =>
                                    ifDropDownList.map(dropdownItem =>
                                      dropdownItem.value === thenValue
                                        ? {
                                            ...dropdownItem,
                                            disabled: false,
                                          }
                                        : dropdownItem
                                    )
                                  )
                                  thenRemove(index)
                                }}
                              >
                                <FiTrash2 />
                              </VmoButton>
                            }
                          />
                        </VmoGridColumn>
                      )}

                      {index !== thenConditionList.length - 1 && (
                        <div
                          style={{
                            width: '100%',
                            textAlign: 'center',
                            margin: '12px 0',
                          }}
                        >
                          <VmoLabel>AND</VmoLabel>
                        </div>
                      )}
                    </VmoGrid>
                  )
                })}

                {thenConditionList.length < 2 && (
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      margin: '12px 0',
                    }}
                  >
                    <VmoButton
                      size="mini"
                      basic
                      onClick={() =>
                        thenAppend({
                          then: null,
                          action: null,
                          thenData: null,
                        })
                      }
                    >
                      Add Condition
                    </VmoButton>
                  </div>
                )}
              </VmoFormField>
            </VmoForm>
          </VmoCardContent>
        </VmoCard>
        <div className="info-header mb-3">
          <VmoCheckbox id="processing" label="Stop processing other rules" />
          <VmoTooltip
            content=" When this option is enabled, all the other rules will be stopped except this"
            trigger={<AiOutlineInfoCircle />}
          />
        </div>
        <br />
      </Page>
    </>
  )
}

export default RuleForm
